import React from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'wouter';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import Checkbox from '@mui/material/Checkbox';

import { loadChildren, deleteChild } from '../childrenReducer';
import ConfirmationDialog from '../../common/components/ConfirmationDialog';
import type { AppDispatch } from '../../store';
import getTableOptions from './tableOptions';
import { DecryptedItem } from '../../common/types/items';
import { Child } from '../../common/types/child';

type Props = {
  searchValue: string;
  children: DecryptedItem<Child>[];
  selectedChildrenIds: string[];
  setSelectedChildrenIds: (ids: string[]) => void;
};

function ChildrenTable({ searchValue, children, selectedChildrenIds, setSelectedChildrenIds }: Props) {
  const dispatch: AppDispatch = useDispatch();
  const [location, setLocation] = useLocation();
  const [sorting, setSorting] = React.useState<{ sortable: string; order: 'ASC' | 'DESC' }>({
    sortable: 'date',
    order: 'DESC',
  });

  const { table, sort, filter } = getTableOptions();

  const filteredChildren = children
    .filter(({ payload }) => filter(searchValue, payload))
    .sort((childA, childB) => sort(childA.payload, childB.payload, sorting));

  return (
    <Paper variant='outlined' sx={{ height: '100%', overflow: 'hidden' }}>
      <TableContainer sx={{ maxHeight: '100%' }}>
        <Table stickyHeader aria-label='sticky table'>
          <TableHead
            sx={{
              '& th': { fontWeight: 'bold', background: '#F2F2F2' },
            }}
          >
            <TableRow>
              {table.map((option) => {
                const { headline, sortable } = option;
                if (option.type === 'select') {
                  return (
                    <TableCell key={`header_${option.headline}_${option.type}`}>
                      <Checkbox
                        checked={children.length === selectedChildrenIds.length}
                        indeterminate={selectedChildrenIds.length > 0 && children.length !== selectedChildrenIds.length}
                        onClick={(event) => event.stopPropagation()}
                        onChange={() => {
                          if (children.length !== selectedChildrenIds.length) {
                            const ids = children.map((child) => child.id);
                            setSelectedChildrenIds(ids);
                          } else {
                            setSelectedChildrenIds([]);
                          }
                        }}
                      />
                    </TableCell>
                  );
                }
                return (
                  <TableCell key={`header_${headline}`}>
                    {headline}
                    {sortable != null && (
                      <IconButton
                        color={sorting.sortable === sortable ? 'primary' : 'secondary'}
                        aria-label='sort'
                        sx={{ marginLeft: '8px' }}
                        onClick={() => {
                          const isSame = sorting.sortable === sortable;
                          const order =
                            isSame && sorting.order === 'ASC'
                              ? 'DESC'
                              : isSame && sorting.order === 'DESC'
                              ? 'ASC'
                              : 'ASC';
                          setSorting({ sortable: sortable, order });
                        }}
                      >
                        {sorting.sortable != sortable && <UnfoldMoreIcon fontSize='small' />}
                        {sorting.sortable === sortable && sorting.order === 'ASC' && (
                          <KeyboardArrowUpIcon fontSize='small' />
                        )}
                        {sorting.sortable === sortable && sorting.order === 'DESC' && (
                          <KeyboardArrowDownIcon fontSize='small' />
                        )}
                      </IconButton>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredChildren.map(({ id, payload }) => (
              <TableRow
                key={id}
                sx={{
                  height: '54px',
                  '&:hover': {
                    cursor: 'pointer',
                    background: '#FAFAFA',
                  },
                  '&:last-child td, &:last-child th': { border: 0 },
                }}
                onClick={() => setLocation(`${location}/${id}`)}
              >
                {table.map((option) => {
                  if (option.type === 'text') {
                    return <TableCell key={`body_${option.headline}`}>{option.value(payload)}</TableCell>;
                  }
                  if (option.type === 'boolean') {
                    return (
                      <TableCell key={`body_${option.headline}`}>
                        {option.value(payload) ? <CheckCircleOutlineIcon /> : <RemoveCircleOutlineIcon />}
                      </TableCell>
                    );
                  }
                  if (option.type === 'delete') {
                    return (
                      <TableCell key={`body_${option.headline}_${option.type}`}>
                        <ConfirmationDialog
                          button={(onClick) => (
                            <IconButton
                              aria-label='add child'
                              onClick={(event) => {
                                event.stopPropagation();
                                onClick();
                              }}
                            >
                              <DeleteIcon fontSize='small' color='primary' />
                            </IconButton>
                          )}
                          dialogTitle='Kinder löschen'
                          dialogText='Möchtest du die Kinder wirklich löschen?'
                          dialogConfirmText='Löschen'
                          onConfirm={async () => {
                            await dispatch(deleteChild(id));
                            await dispatch(loadChildren());
                          }}
                        />
                      </TableCell>
                    );
                  }
                  if (option.type === 'select') {
                    const isChecked = selectedChildrenIds.includes(id);
                    return (
                      <TableCell key={`body_${option.headline}_${option.type}`}>
                        <Checkbox
                          checked={isChecked}
                          onClick={(event) => event.stopPropagation()}
                          onChange={() => {
                            const newSelectedChildren = isChecked
                              ? selectedChildrenIds.filter((childrenId) => childrenId !== id)
                              : selectedChildrenIds.concat([id]);
                            setSelectedChildrenIds(newSelectedChildren);
                          }}
                        />
                      </TableCell>
                    );
                  }
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export default ChildrenTable;
